<template>
    <VTooltip
        v-if="isMounted"
        :theme="props.theme"
        :triggers="triggers"
        :shown="shown"
    >
        <slot />
        <template #popper>
            <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
            <Typography
                type="label"
                size="m"
                :accent="true"
                :responsive="false"
                v-html="props.content"
            />
            <!-- eslint-enable vue/no-v-html vue/no-v-text-v-html-on-component -->
        </template>
    </VTooltip>
    <div v-else>
        <slot />
    </div>
</template>
<script setup lang="ts">
import { type TriggerEvent, Tooltip as VTooltip } from 'floating-vue'
import { computed } from 'vue'

import Typography from '@/components_new/Typography.vue'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

interface TooltipProps {
    content?: string
    shown?: boolean
    theme?: 'black-tooltip' | 'accent-tooltip'
}

const props = withDefaults(defineProps<TooltipProps>(), {
    shown: undefined,
    theme: 'accent-tooltip',
})
const triggers = computed((): TriggerEvent[] => {
    if (props.shown !== undefined || !props.content) {
        return []
    }
    return ['hover', 'focus']
})

const isMounted = useIsMounted()
</script>
<style>
.v-popper__popper.v-popper--theme-accent-tooltip .v-popper__wrapper,
.v-popper__popper.v-popper--theme-black-tooltip .v-popper__wrapper {
    white-space: nowrap;
    min-width: 30px;
    text-align: center;
    opacity: 1;
}

.v-popper__popper.v-popper--theme-accent-tooltip .v-popper__wrapper {
    border-radius: 1000px;
}

.v-popper__popper.v-popper--theme-black-tooltip .v-popper__wrapper {
    border-radius: 12px;
}

.v-popper__popper.v-popper--theme-accent-tooltip .v-popper__wrapper {
    color: var(--black);
    background-color: var(--electric-lime);
}

.v-popper__popper.v-popper--theme-black-tooltip .v-popper__wrapper {
    color: var(--white);
    background-color: var(--carbon-400);
}

.v-popper__popper.v-popper--theme-accent-tooltip .v-popper__inner,
.v-popper__popper.v-popper--theme-black-tooltip .v-popper__inner {
    color: inherit;
    background-color: inherit;
    border-radius: inherit;
    padding: 8px 12px;
}

.v-popper__popper[data-popper-placement^='top'].v-popper--theme-accent-tooltip .v-popper__arrow-outer,
.v-popper__popper[data-popper-placement^='top'].v-popper--theme-black-tooltip .v-popper__arrow-outer {
    top: -1px;
}

.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-accent-tooltip .v-popper__arrow-container,
.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-black-tooltip .v-popper__arrow-container {
    top: 1px;
}

.v-popper__popper[data-popper-placement^='left'].v-popper--theme-accent-tooltip .v-popper__arrow-container,
.v-popper__popper[data-popper-placement^='left'].v-popper--theme-black-tooltip .v-popper__arrow-container {
    right: -9px;
}

.v-popper__popper[data-popper-placement^='right'].v-popper--theme-accent-tooltip .v-popper__arrow-container,
.v-popper__popper[data-popper-placement^='right'].v-popper--theme-black-tooltip .v-popper__arrow-container {
    left: 1px;
}

.v-popper__popper.v-popper--theme-accent-tooltip .v-popper__wrapper .v-popper__arrow-container .v-popper__arrow-outer {
    border-color: var(--electric-lime);
}

.v-popper__popper.v-popper--theme-black-tooltip .v-popper__wrapper .v-popper__arrow-container .v-popper__arrow-outer {
    border-color: var(--carbon-400);
}

.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-accent-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-accent-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-black-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-black-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper {
    transform: translate(0, 8px);
    transition: transform 0.1s ease-out;
}

.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-accent-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-accent-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-black-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='bottom'].v-popper--theme-black-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper {
    transform: translate(0, 13px);
    transition: transform 0.2s ease-out;
}

.v-popper__popper[data-popper-placement^='top'].v-popper--theme-accent-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='top'].v-popper--theme-accent-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='top'].v-popper--theme-black-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='top'].v-popper--theme-black-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper {
    transform: translate(0, 5px);
    transition: transform 0.1s ease-out;
}

.v-popper__popper[data-popper-placement^='top'].v-popper--theme-accent-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='top'].v-popper--theme-accent-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='top'].v-popper--theme-black-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='top'].v-popper--theme-black-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper {
    transform: translate(0, 0);
    transition: transform 0.2s ease-out;
}

.v-popper__popper[data-popper-placement^='left'].v-popper--theme-accent-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='left'].v-popper--theme-accent-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='left'].v-popper--theme-black-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='left'].v-popper--theme-black-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper {
    transform: translate(-8px);
    transition: transform 0.2s ease-out;
}

.v-popper__popper[data-popper-placement^='left'].v-popper--theme-accent-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='left'].v-popper--theme-accent-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='left'].v-popper--theme-black-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='left'].v-popper--theme-black-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper {
    transform: translate(-3px);
    transition: transform 0.1s ease-out;
}

.v-popper__popper[data-popper-placement^='right'].v-popper--theme-accent-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='right'].v-popper--theme-accent-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='right'].v-popper--theme-black-tooltip.v-popper__popper--show-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='right'].v-popper--theme-black-tooltip.v-popper__popper--hide-from
    .v-popper__wrapper {
    transform: translate(3px);
    transition: transform 0.2s ease-out;
}

.v-popper__popper[data-popper-placement^='right'].v-popper--theme-accent-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='right'].v-popper--theme-accent-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='right'].v-popper--theme-black-tooltip.v-popper__popper--show-from
    .v-popper__wrapper,
.v-popper__popper[data-popper-placement^='right'].v-popper--theme-black-tooltip.v-popper__popper--hide-to
    .v-popper__wrapper {
    transform: translate(8px);
    transition: transform 0.1s ease-out;
}
</style>
